import type { Annotation, Note } from "building-types/annotation/annotationTypes";
import lruMemo from "common/utils/lruMemo";
import { camelCaseToTitleCase } from "common/utils/strings";
import type { ParameterReplacementValueContext } from "doc/parameterTools";
import {
  parameterTokenNew,
  parameterIdRegex,
  getParameterReplacementValue,
  parameterTokenRegex,
  isParameterRef,
} from "doc/parameterTools";

const ANNOTATION_PARAMETER_REF_EVALUATION_CACHE_SIZE = 100;

/**
 * This function replaces instances of parameter references in annotations with the actual values.
 * A Parameter ref in annotation looks like `{{ parameterId }}` and has different result depending on
 * the type of the parameter:
 *
 * - TEXT and URL parameters are replaced with the text
 * - NUMBER and BOOLEAN parameters are replaced with the parameter value formatted with the JavaScript toString method
 * - FOOT_INCH parameters are replaced with the formatted foot inch fraction by `formatInchesFraction` w/ 1/32 precision
 * - DRAWING_SCALE parameters are replaced with the formatted view scale factor by `formatViewScaleFactor`
 * - LEVEL, OPTION_STATE, and VIEW_TEMPLATE parameters are replaced with the name of the corresponding entity
 *
 * This function is memoized to avoid recomputing the same text geometry
 */
function evaluateAnnotationParameterRefsImpl(args: ParameterReplacementValueContext & { annotation: Note }): Note {
  const text = args.annotation.text.replace(parameterTokenRegex, (matchStr) => {
    const id = matchStr.slice(2, -2).trim();
    if (!(id in args.params)) {
      return matchStr;
    }
    const replacement: string | undefined = getParameterReplacementValue(id, args);
    if (replacement !== undefined) {
      return replacement;
    }

    // When the parameter is bound to a reference that resolved to nothing, name the referenced
    // parameter rather than this one — that is the parameter the user pointed at, and it matches
    // what a `{{ referencedName }}` token written directly into the text would render as.
    const value = args.values[id];
    const unresolvedId = isParameterRef(value) ? value.id : id;
    const { name } = args.params[unresolvedId] ?? args.params[id];

    return parameterReplacementFallback(unresolvedId, name);
  });

  if (text === args.annotation.text) {
    return args.annotation;
  }

  return {
    ...args.annotation,
    text,
  };
}

function parameterReplacementFallback(parameterId: string, name: string): string {
  if (parameterId === "viewportNumber") {
    return "#";
  }

  if (parameterId === "viewportName" || parameterId === "viewportDescription") {
    return camelCaseToTitleCase(parameterId);
  }

  return parameterTokenNew(name);
}

const memoizedEvaluateAnnotationParameterRefs = lruMemo(
  evaluateAnnotationParameterRefsImpl,
  ANNOTATION_PARAMETER_REF_EVALUATION_CACHE_SIZE,
);

export function evaluateAnnotationParameterRefs<T extends Annotation>(
  annotation: T,
  args: ParameterReplacementValueContext,
): T {
  if (annotation.type !== "NOTE") {
    return annotation;
  }
  return memoizedEvaluateAnnotationParameterRefs({ ...args, annotation }) as T;
}

export function replaceParamIdsInNoteText(
  note: Annotation,
  paramIdMapping: { [paramId: string]: string /* new param id */ },
) {
  if (note.type !== "NOTE") {
    return note;
  }

  let text = note.text;

  for (const id in paramIdMapping) {
    const regex = parameterIdRegex(id);
    text = text.replace(regex, parameterTokenNew(paramIdMapping[id]));
  }

  return {
    ...note,
    text,
  };
}
